import React, { Component } from "react";
import { getFiles, deleteDocument, replaceDocument, fetchProcessingStatus, downloadFile, fetchDepartments } from "../../../Actions/KnowledgeBaseBuilder";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import UploadProgressTracker from "./UploadProgressTracker";
import ArrowLeftSVG from "../../../Assets/svg/ArrowLeftSVG.svg";
import { EllipsisVertical, Eye, FileX, Pencil, Trash2, ChevronDown, X } from "lucide-react";
import TableLoader from "../../Common/TableLoader";
import Tooltip from "../../Common/Tooltip";
import { getCached, setCached, clearCached } from "../../../Utils/fileCache";

const KB_CACHE_KEY = (page, department) => `knowledge-base-files-p${page}-d${department || 'all'}`;

class FileManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      files: [],
      currentPage: 1,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false,
      totalCount: 0,
      itemsPerPage: null,
      openActionMenuId: null,
      pageInput: "1",
      loading: true,
      deletingFileId: null,
      deleteConfirmFile: null,
      replaceModalFile: null,
      replaceFile: null,
      replacing: false,
      replaceProgress: null, // { status, stage, file_name, error }
      departments: [],
      selectedDepartment: null,
      departmentDropdownOpen: false,
    };
    this.actionMenuRef = React.createRef();
    this.departmentDropdownRef = React.createRef();
    this._replaceSocket = null;
    this._pollTimer = null;
  }

  static propTypes = {
    getFiles: PropTypes.func.isRequired,
    deleteDocument: PropTypes.func.isRequired,
    replaceDocument: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.fetchFiles(1);
    this.props.fetchDepartments().then((data) => {
      if (data?.departments) this.setState({ departments: data.departments });
    });
    document.addEventListener("mousedown", this.handleClickOutside);

    // Reconnect progress tracker if a file replacement is still processing
    const userId = this.props.currentUser?.id;
    if (userId) {
      this.props.fetchProcessingStatus().then((data) => {
        const inProgress = data?.processing?.[0];
        if (inProgress) {
          this._openReplaceSocket(userId, inProgress.file_name);
          this.setState({
            replaceProgress: {
              status: "PROCESSING",
              stage: "ingesting",
              file_id: inProgress.file_id,
              file_name: inProgress.file_name,
            },
          });
        }
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    if (this._replaceSocket) {
      this._replaceSocket.close();
      this._replaceSocket = null;
    }
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  _openReplaceSocket = (userId, fileName) => {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const host = window.location.host;
    const ws = new WebSocket(`${protocol}://${host}/ws/file-ingest/${userId}/`);
    this._replaceSocket = ws;

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        // Only handle events for this specific file
        if (data.file_name && data.file_name !== fileName) return;
        this.setState({ replaceProgress: data });
        if (data.status === "COMPLETED") {
          ws.close();
          this._replaceSocket = null;
          clearCached("knowledge-base-files");
          setTimeout(() => {
            this.setState({ replaceModalFile: null, replaceFile: null, replaceProgress: null });
            this.fetchFiles(this.state.currentPage);
          }, 2000);
        }
        if (data.status === "FAILED") {
          ws.close();
          this._replaceSocket = null;
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onerror = () => ws.close();
  };

  handleClickOutside = (e) => {
    if (
      this.state.openActionMenuId !== null &&
      this.actionMenuRef.current &&
      !this.actionMenuRef.current.contains(e.target)
    ) {
      this.setState({ openActionMenuId: null });
    }
    if (
      this.state.departmentDropdownOpen &&
      this.departmentDropdownRef.current &&
      !this.departmentDropdownRef.current.contains(e.target)
    ) {
      this.setState({ departmentDropdownOpen: false });
    }
  };

  applyResponse = (response, page) => {
    const resultsCount = response.results ? response.results.length : 0;
    const totalCount = response.count || 0;
    let itemsPerPage;
    if (page === 1 || !this.state.itemsPerPage) {
      itemsPerPage = resultsCount > 0 ? resultsCount : 5;
    } else {
      itemsPerPage = this.state.itemsPerPage;
    }
    const baseItemsPerPage = page === 1 ? itemsPerPage : this.state.itemsPerPage || itemsPerPage;
    const totalPages = totalCount > 0 ? Math.ceil(totalCount / baseItemsPerPage) : 1;
    this.setState({
      files: response.results || [],
      currentPage: page,
      totalPages,
      totalCount,
      itemsPerPage: page === 1 ? itemsPerPage : this.state.itemsPerPage,
      hasNext: response.next !== null,
      hasPrevious: response.previous !== null,
      loading: false,
    }, this._updatePolling);
  };

  _updatePolling = () => {
    const hasActive = this.state.files.some(
      (f) => f.processing_status === "PROCESSING" || f.processing_status === "PENDING"
    );
    if (hasActive && !this._pollTimer) {
      this._pollTimer = setInterval(this._pollStatus, 8000);
    } else if (!hasActive && this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  };

  _pollStatus = async () => {
    const { currentPage, selectedDepartment } = this.state;
    const cacheKey = KB_CACHE_KEY(currentPage, selectedDepartment);
    try {
      const response = await this.props.getFiles(currentPage, selectedDepartment);
      if (response) {
        setCached(cacheKey, response);
        this.applyResponse(response, currentPage);
      }
    } catch (_) {}
  };

  fetchFiles = async (page) => {
    const { selectedDepartment } = this.state;
    const cacheKey = KB_CACHE_KEY(page, selectedDepartment);
    const cached = getCached(cacheKey);
    if (cached) {
      this.applyResponse(cached, page);
      this.props.getFiles(page, selectedDepartment).then((response) => {
        if (response) {
          setCached(cacheKey, response);
          this.applyResponse(response, page);
        }
      }).catch(() => {});
      return;
    }

    this.setState({ loading: true });
    try {
      const response = await this.props.getFiles(page, selectedDepartment);
      if (response) {
        setCached(cacheKey, response);
        this.applyResponse(response, page);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
      this.setState({ loading: false });
    }
  };

  handleDepartmentFilter = (dept) => {
    this.setState(
      { selectedDepartment: dept, currentPage: 1, pageInput: "1", departmentDropdownOpen: false },
      () => this.fetchFiles(1)
    );
  };

  clearDepartmentFilter = () => {
    this.setState(
      { selectedDepartment: null, currentPage: 1, pageInput: "1" },
      () => this.fetchFiles(1)
    );
  };

  handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= this.state.totalPages) {
      this.setState({ currentPage: newPage, pageInput: String(newPage) }, () => {
        this.fetchFiles(newPage);
      });
    }
  };

  toggleActionMenu = (fileId) => {
    this.setState((prevState) => ({
      openActionMenuId: prevState.openActionMenuId === fileId ? null : fileId,
    }));
  };

  handleView = (file) => {
    this.setState({ openActionMenuId: null });
    const path = file.remote_path || file.file_name;
    this.props.downloadFile(path);
  };

  fetchFilesSilent = async (page) => {
    const { selectedDepartment } = this.state;
    const cacheKey = KB_CACHE_KEY(page, selectedDepartment);
    try {
      const response = await this.props.getFiles(page, selectedDepartment);
      if (response) {
        setCached(cacheKey, response);
        this.applyResponse(response, page);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  };

  handleDeleteClick = (file) => {
    this.setState({ openActionMenuId: null, deleteConfirmFile: file });
  };

  handleDeleteConfirm = async () => {
    const file = this.state.deleteConfirmFile;
    if (!file) return;
    this.setState({ deleteConfirmFile: null, deletingFileId: file.id });
    this.setState((prev) => ({ files: prev.files.filter((f) => f.id !== file.id) }));
    try {
      await this.props.deleteDocument({
        filename: file.file_name,
        department_name: file.department_name,
        subdepartment_name: file.subdepartment_name,
      });
      clearCached("knowledge-base-files");
      this.fetchFilesSilent(this.state.currentPage);
    } catch (err) {
      console.error("Delete from Milvus failed:", err);
      this.fetchFiles(this.state.currentPage);
    } finally {
      this.setState({ deletingFileId: null });
    }
  };

  handleDeleteCancel = () => {
    this.setState({ deleteConfirmFile: null });
  };

  handleOpenReplaceModal = (file) => {
    this.setState({ openActionMenuId: null, replaceModalFile: file, replaceFile: null });
  };

  handleReplaceFileChange = (e) => {
    this.setState({ replaceFile: e.target.files[0] });
  };

  handleReplaceSubmit = async () => {
    const { replaceModalFile, replaceFile } = this.state;
    if (!replaceFile) return;

    this.setState({ replacing: true, replaceProgress: null });

    const userId = this.props.currentUser?.id;
    if (userId) this._openReplaceSocket(userId, replaceModalFile.file_name);

    try {
      const formData = new FormData();
      formData.append("file", replaceFile);
      formData.append("filename", replaceModalFile.file_name);
      formData.append("department_name", replaceModalFile.department_name || "");
      if (replaceModalFile.subdepartment_name) {
        formData.append("subdepartment_name", replaceModalFile.subdepartment_name);
      }
      await this.props.replaceDocument(formData);
      // HTTP 202 received — Celery task is running, show progress tracker
      this.setState({
        replacing: false,
        replaceProgress: {
          status: "PROCESSING",
          stage: "ingesting",
          file_name: replaceModalFile.file_name,
        },
      });
    } catch (err) {
      console.error("Replace failed:", err);
      if (this._replaceSocket) { this._replaceSocket.close(); this._replaceSocket = null; }
      this.setState({ replacing: false, replaceProgress: null });
    }
  };

  truncateFileName = (fileName, maxLength = 20) => {
    if (fileName.length <= maxLength) return fileName;
    const extensionIndex = fileName.lastIndexOf(".");
    const extension = extensionIndex !== -1 ? fileName.slice(extensionIndex) : "";
    const nameWithoutExt = extensionIndex !== -1 ? fileName.slice(0, extensionIndex) : fileName;
    const truncatedName = nameWithoutExt.slice(0, maxLength - extension.length - 3);
    return `${truncatedName}...${extension}`;
  };

  handlePageInputChange = (e) => {
    this.setState({ pageInput: e.target.value });
  };

  handlePageInputSubmit = (e) => {
    if (e.key === "Enter") {
      const page = parseInt(this.state.pageInput, 10);
      if (!isNaN(page) && page >= 1 && page <= this.state.totalPages) {
        this.handlePageChange(page);
      } else {
        this.setState({ pageInput: String(this.state.currentPage) });
      }
      e.target.blur();
    }
  };

  handlePageInputBlur = () => {
    const page = parseInt(this.state.pageInput, 10);
    if (!isNaN(page) && page >= 1 && page <= this.state.totalPages) {
      this.handlePageChange(page);
    } else {
      this.setState({ pageInput: String(this.state.currentPage) });
    }
  };

  renderPagination = () => {
    const { currentPage, totalPages } = this.state;

    return (
      <div className="flex justify-between items-center mt-2 pt-4">
        {/* Previous Button */}
        <button
          onClick={() => this.handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <img className="w-4 h-4" src={ArrowLeftSVG} alt="Previous" />
          Previous
        </button>

        {/* Page Input */}
        <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
          <span>Page</span>
          <input
            type="text"
            value={this.state.pageInput ?? currentPage}
            onChange={this.handlePageInputChange}
            onKeyDown={this.handlePageInputSubmit}
            onBlur={this.handlePageInputBlur}
            onFocus={(e) => e.target.select()}
            className="w-12 h-9 text-center border border-gray-200 rounded-lg text-sm font-medium text-gray-900 outline-none focus:border-primary-blue transition-colors"
          />
          <span>of {totalPages}</span>
        </div>

        {/* Next Button */}
        <button
          onClick={() => this.handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Next
          <img className="rotate-180 w-4 h-4" src={ArrowLeftSVG} alt="Next" />
        </button>
      </div>
    );
  };

  render() {
    const { openActionMenuId, loading, departments, selectedDepartment, departmentDropdownOpen } = this.state;

    return (
      <div className="flex flex-col h-full">
        {/* Table */}
        <div className="flex-1 bg-white border border-blue-100 rounded-xl flex flex-col">
          {/* Title bar */}
          <div className="px-6 py-4 border-b border-blue-100 rounded-t-xl flex items-center justify-between gap-4" style={{ background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)" }}>
            <p className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
              Files ({this.state.totalCount ?? 0})
            </p>
            {/* Department filter */}
            {departments.length > 0 && (
              <div className="flex items-center gap-2">
                {selectedDepartment && (
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700">
                    {selectedDepartment}
                    <button onClick={this.clearDepartmentFilter} className="ml-0.5 hover:text-blue-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                <div className="relative" ref={this.departmentDropdownRef}>
                  <button
                    onClick={() => this.setState(s => ({ departmentDropdownOpen: !s.departmentDropdownOpen }))}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Filter by Department
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${departmentDropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {departmentDropdownOpen && (
                    <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[180px] max-h-56 overflow-y-auto">
                      <button
                        onClick={() => this.handleDepartmentFilter(null)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${!selectedDepartment ? "text-blue-700 font-medium bg-blue-50/50" : "text-gray-700"}`}
                      >
                        All Departments
                      </button>
                      {departments.map((dept) => (
                        <button
                          key={dept}
                          onClick={() => this.handleDepartmentFilter(dept)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${selectedDepartment === dept ? "text-blue-700 font-medium bg-blue-50/50" : "text-gray-700"}`}
                        >
                          {dept}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Column headers */}
          <div className="grid grid-cols-[1.2fr_1fr_1fr_0.8fr_0.8fr_0.7fr_0.5fr] items-center py-2 bg-gray-50/50 px-5 gap-4 border-b border-gray-100">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">File Name</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Date Uploaded</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Uploaded By</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Department</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Sub-Department</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Status</div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider text-center">Actions</div>
          </div>

          {/* File Rows */}
          {loading ? (
            <TableLoader />
          ) : this.state.files.length > 0 ? (
            this.state.files.map((file) => (
              <div
                key={file.id}
                className="grid grid-cols-[1.2fr_1fr_1fr_0.8fr_0.8fr_0.7fr_0.5fr] py-3.5 px-5 gap-4 border-t border-gray-100 font-inter text-sm text-gray-700 hover:bg-blue-50/40 transition-colors"
              >
                <div className="font-medium text-gray-900 overflow-visible">
                  <Tooltip text={file.file_name} icon={false}>
                    <span className="cursor-default">{this.truncateFileName(file.file_name)}</span>
                  </Tooltip>
                </div>
                <div className="text-gray-500">
                  {new Date(file.uploaded_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
                <div className="text-gray-500 truncate">
                  {file.uploaded_by || <span className="text-gray-400">—</span>}
                </div>
                <div className="truncate">
                  {file.department_name || <span className="text-gray-400">—</span>}
                </div>
                <div className="truncate">
                  {file.subdepartment_name || <span className="text-gray-400">—</span>}
                </div>
                <div>
                  {(() => {
                    const s = file.processing_status;
                    const isActive = s === "PROCESSING" || s === "PENDING";
                    const label =
                      s === "COMPLETED"  ? "Available" :
                      s === "FAILED"     ? "Failed" :
                      s === "PROCESSING" ? "Processing..." :
                      s === "PENDING"    ? "Queued" : (s || "—");
                    const colorClass =
                      s === "COMPLETED" ? "bg-green-50 text-green-700" :
                      s === "FAILED"    ? "bg-red-50 text-red-700" :
                      s               ? "bg-yellow-50 text-yellow-700" : "text-gray-400";
                    return s ? (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
                        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />}
                        {label}
                      </span>
                    ) : <span className="text-gray-400">—</span>;
                  })()}
                </div>
                <div ref={openActionMenuId === file.id ? this.actionMenuRef : null} className="relative flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => this.toggleActionMenu(file.id)}
                    className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
                    aria-label="More actions"
                  >
                    <EllipsisVertical className="w-4 h-4" />
                  </button>
                  {openActionMenuId === file.id && (
                    <div className="absolute right-0 top-8 z-20 w-32 bg-white border border-gray-100 rounded-lg shadow-custom-shadow py-1 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => this.handleView(file)}
                        className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-primary-blue transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => this.handleOpenReplaceModal(file)}
                        className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-primary-blue transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                        Update
                      </button>
                      <button
                        type="button"
                        onClick={() => this.handleDeleteClick(file)}
                        disabled={this.state.deletingFileId === file.id}
                        className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        {this.state.deletingFileId === file.id ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-1 w-full flex-col items-center justify-center gap-6 py-12">
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)" }}>
                <FileX className="w-10 h-10 text-blue-400" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-600">
                  No files uploaded yet
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Upload a file or folder to get started
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Pagination — always at bottom */}
        <div className="mt-auto pt-4 pb-6">
          {this.renderPagination()}
        </div>

        {/* Delete Confirmation Modal */}
        {this.state.deleteConfirmFile && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Delete File</h3>
                  <p className="text-sm text-gray-500 mt-0.5">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                Are you sure you want to delete{" "}
                <span className="font-medium text-gray-900">
                  {this.truncateFileName(this.state.deleteConfirmFile.file_name, 40)}
                </span>
                ? It will be removed from the knowledge base permanently.
              </p>
              <div className="flex justify-end gap-3 mt-1">
                <button
                  type="button"
                  onClick={this.handleDeleteCancel}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={this.handleDeleteConfirm}
                  className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Replace Modal */}
        {this.state.replaceModalFile && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md flex flex-col gap-4">
              {this.state.replaceProgress ? (
                <UploadProgressTracker
                  fileName={this.state.replaceProgress.file_name}
                  status={this.state.replaceProgress.status}
                  stage={this.state.replaceProgress.stage}
                  error={this.state.replaceProgress.error}
                />
              ) : (
                <>
                  <div className="font-semibold text-gray-900 text-lg">Replace File</div>
                  <p className="text-sm text-gray-500">
                    Select a new file to replace <span className="font-medium text-gray-700">{this.state.replaceModalFile.file_name}</span>
                  </p>
                  <input
                    type="file"
                    onChange={this.handleReplaceFileChange}
                    className="text-sm text-gray-700 border border-gray-200 rounded-lg p-2"
                  />
                  <div className="flex justify-end gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => this.setState({ replaceModalFile: null, replaceFile: null })}
                      className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={this.handleReplaceSubmit}
                      disabled={!this.state.replaceFile || this.state.replacing}
                      className="px-4 py-2 text-sm rounded-lg bg-primary-blue text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {this.state.replacing ? "Replacing…" : "Replace"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

// Connect Redux
const mapStateToProps = (state) => ({
  files: state.knowledgeBaseBuilder.files,
  currentUser: state.authentication?.user,
});

const mapDispatchToProps = (dispatch) => ({
  getFiles: (page, department) => dispatch(getFiles(page, department)),
  deleteDocument: (payload) => dispatch(deleteDocument(payload)),
  replaceDocument: (formData) => dispatch(replaceDocument(formData)),
  fetchProcessingStatus: () => dispatch(fetchProcessingStatus()),
  downloadFile: (fileName) => dispatch(downloadFile(fileName)),
  fetchDepartments: () => dispatch(fetchDepartments()),
});

export default connect(mapStateToProps, mapDispatchToProps)(FileManager);
